<template>
    <div
        class="app-tile"
        :class="{
            'app-tile-active': isActive,
            'app-tile-default': app.is_default,
            'app-tile-disabled': isDisabled
        }"
        :data-app-id="app.id"
        @click="!isDisabled && $emit('click')"
        @contextmenu="!isDisabled && $emit('contextmenu', $event)"
        @mouseover="!isDisabled && $emit('mouseover')"
    >
        <img
            :src="imageSrc"
            :alt="app.name"
            class="app-icon"
            @error="handleImageError"
            ref="imageRef"
        >

        <div
            class="app-name-overlay"
            v-show="imageError"
        >
            {{ app.name }}
        </div>

        <div v-if="app.is_default" class="default-indicator">
            <img class="default-img" src="/images/Buzztime_Buddies_Logo_500px-white.png" alt=""/>
        </div>
        <div v-if="isDisabled" class="disabled-indicator">{{ app.disabled_reason }}</div>
        <div v-if="!isDisabled && app.status && app.status !== 'PRD'" class="env-indicator">{{ app.status.toUpperCase() }}</div>

        <!-- Auto-launch progress bar for default app -->
        <div v-if="app.is_default && app.autoLaunchProgress !== undefined" class="auto-launch-progress-container">
            <div class="auto-launch-progress-bar" :style="{ width: `${app.autoLaunchProgress * 100}%` }"></div>
        </div>
    </div>
</template>

<script setup>
import {ref, computed} from 'vue';
import {defaultIcon} from "@/apps.js";

const props = defineProps({
    app: {
        type: Object,
        required: true
    },
    isActive: {
        type: Boolean,
        default: false
    }
});

const imageError = ref(false);
const imageRef = ref(null);

// Check if app is disabled
const isDisabled = computed(() => {
    return props.app.disabled === true;
});

// Get disabled text - show COMING SOON for disabled apps
const disabledText = computed(() => {
    return props.app.disabled ? 'COMING SOON' : '';
});

// Function to get tile image path
const getTileImagePath = (iconName) => {
    return `/cards/tiles/${iconName}`;
};

// Computed property for image source
const imageSrc = computed(() => {
    if (imageError.value) {
        return `/cards/tiles/default.webp`;
    }
    return getTileImagePath(props.app.icon_url);
});

const handleImageError = () => {
    if (imageError.value) return; // default card failed too — nothing left to try
    imageError.value = true;
};
</script>

<style scoped>
.app-tile {
    opacity: 0;
    aspect-ratio: 1/1.5;
	width: 100%;
	height: auto;
	box-sizing: border-box;
    border-radius: var(--border-radius-xl);
    position: relative;
    cursor: pointer;
    transition: all var(--transition-normal);
    transform-origin: center;
}

.app-tile:hover {
    transform: scale(1.1) !important;
    z-index: 1;
}

.app-tile-active {
    transform: scale(1.1) !important;
    box-shadow: 0 0 20px rgba(34, 177, 246, 0.5);
    border: var(--border-norm) var(--color-primary-light);
    z-index: 2;
}

.app-tile-default {
    background: linear-gradient(135deg, rgba(28, 95, 230, 0.2), rgba(34, 177, 246, 0.2));
}

.app-tile-disabled {
    cursor: not-allowed;
    pointer-events: none;
}

.app-tile-disabled img {
    filter: brightness(0.5);
}

.app-tile-disabled:hover {
    transform: none !important;
}

.app-icon {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: var(--border-radius-xl);
}

.app-name-overlay {
    position: absolute;
    display: flex;
    bottom: 0;
    left: 0;
    width: 100%;
    align-items: center;
    justify-content: center;
    height: 5rem;
    padding: var(--spacing-lg);
    background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 50%, transparent 100%);
    color: white;
    font-size: var(--font-size-reg);
    font-weight: var(--font-weight-semibold);
    text-align: center;
    border-bottom-left-radius: var(--border-radius-lg);
    border-bottom-right-radius: var(--border-radius-lg);
}

.default-indicator, .disabled-indicator, .env-indicator {
    position: absolute;
    top: var(--spacing-xs);
    right: var(--spacing-xs);
    width: fit-content;
    color: var(--color-text);
    font-size: var(--font-size-reg);
    font-weight: var(--font-weight-semibold);
	text-align: center;
    padding: calc(var(--spacing-xs) * 0.4) var(--spacing-sm);
    border-radius: var(--border-radius-full);
    z-index: 3;
    text-shadow: var(--shadow-sm);
	filter: drop-shadow(var(--shadow-sm));
}

.default-img {
    width: var(--font-size-title);
    height: auto;
}

.default-indicator {
	padding: 0;
}

.disabled-indicator {
    background: var(--gradient-secondary);
	width: calc(100% - (2 * var(--spacing-xs)));
}

.env-indicator {
    background: var(--gradient-internal);
    left: var(--spacing-xs);
    /* right: var(--spacing-xs);
    width: calc(100% - (2 * var(--spacing-xs)));
    white-space: nowrap;
    text-align: center; */
}

/* Auto-launch progress bar */
.auto-launch-progress-container {
    position: absolute;
    bottom: var(--spacing-xs);
    left: var(--spacing-xs);
    right: var(--spacing-xs);
    height: var(--font-size-sm);
    background-color: var(--color-border);
    border-radius: var(--border-radius-full);
    overflow: hidden;
}

.auto-launch-progress-bar {
    height: 100%;
    background: var(--gradient-primary);
    border-radius: var(--border-radius-full);
    width: 0;
    min-width: 7% !important;
    transition: width var(--transition-linear);
}
</style>